<template>
    <article class="article-content">
        <section class="content-section">
            <h2>{{ sections?.intro?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.intro?.content)"
                :key="'intro-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.free_paths?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.free_paths?.content)"
                :key="'free-paths-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/hierarchies/new_customer_new_path-400w.webp   400w,
                            /images/blog/hierarchies/new_customer_new_path-800w.webp   800w,
                            /images/blog/hierarchies/new_customer_new_path-1200w.webp 1200w,
                            /images/blog/hierarchies/new_customer_new_path-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/hierarchies/new_customer_new_path.png"
                        alt="Creating a new customer entity with a custom path"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.new_customer_path }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.auto_parents?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.auto_parents?.content)"
                :key="'auto-parents-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/hierarchies/new_order_existing_path-400w.webp   400w,
                            /images/blog/hierarchies/new_order_existing_path-800w.webp   800w,
                            /images/blog/hierarchies/new_order_existing_path-1200w.webp 1200w,
                            /images/blog/hierarchies/new_order_existing_path-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/hierarchies/new_order_existing_path.png"
                        alt="Path autocomplete showing existing entities"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.path_autocomplete }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.virtual_folders?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.virtual_folders?.content)"
                :key="'virtual-folders-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.relationships?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.relationships?.content)"
                :key="'relationships-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/hierarchies/order_with_parent_in_tree-400w.webp   400w,
                            /images/blog/hierarchies/order_with_parent_in_tree-800w.webp   800w,
                            /images/blog/hierarchies/order_with_parent_in_tree-1200w.webp 1200w,
                            /images/blog/hierarchies/order_with_parent_in_tree-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/hierarchies/order_with_parent_in_tree.png"
                        alt="Entity tree showing parent-child relationships"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.entity_tree }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.import_integration?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.import_integration?.content)"
                :key="'import-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/hierarchies/import_definition_with_path-400w.webp   400w,
                            /images/blog/hierarchies/import_definition_with_path-800w.webp   800w,
                            /images/blog/hierarchies/import_definition_with_path-1200w.webp 1200w,
                            /images/blog/hierarchies/import_definition_with_path-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/hierarchies/import_definition_with_path.png"
                        alt="Import definition with path configuration"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.import_definition }}</figcaption>
            </figure>
            <figure class="article-figure article-figure--narrow">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/hierarchies/random_user_list-400w.webp   400w,
                            /images/blog/hierarchies/random_user_list-800w.webp   800w,
                            /images/blog/hierarchies/random_user_list-1200w.webp 1200w,
                            /images/blog/hierarchies/random_user_list-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 70vw, (max-width: 1200px) 56vw, 560px"
                    />
                    <img
                        src="/images/blog/hierarchies/random_user_list.png"
                        alt="Example user data for import"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.source_data }}</figcaption>
            </figure>
        </section>

        <section class="content-section teaser-section">
            <h2>{{ sections?.teaser?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.teaser?.content)"
                :key="'teaser-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="coming-soon-badge">
                <SmartIcon
                    icon="clock"
                    :size="18"
                />
                {{ t('blog.coming_soon') }}: Import/Export Workflow Definitions
            </div>
        </section>
    </article>
</template>

<script setup lang="ts">
    import {
        useBlogFormatter,
        type PostSections,
        type PostCaptions,
    } from '@/composables/useBlogFormatter'
    import { useTranslations } from '@/composables/useTranslations'
    import SmartIcon from '@/components/common/SmartIcon.vue'

    defineProps<{
        sections?: PostSections
        captions?: PostCaptions
    }>()

    const { t } = useTranslations()
    const { splitParagraphs, formatContent } = useBlogFormatter()
</script>
